import { useEffect, useRef, useCallback } from 'react'
import { z } from 'zod'

const codeSchema = z.string().min(4).max(8)

export default function useOtpInputs(
	otp: string[],
	setOtp: (o: string[]) => void,
	onVerify: (code: string) => void
) {
	const refs = useRef<(HTMLInputElement | null)[]>([])
	if (refs.current.length !== otp.length) refs.current = Array(otp.length).fill(null)

	useEffect(() => {
		refs.current[0]?.focus()
	}, [])

	const isOtpComplete = useCallback((arr: string[]) => arr.every((d) => d.length === 1), [])
	const isCodeValid = useCallback(
		(arr: string[]) => codeSchema.safeParse(arr.join('').trim()).success,
		[]
	)

	const handleOtpChange = useCallback(
		(val: string, idx: number) => {
			const sanitized = val.replace(/[^0-9]/g, '')
			const next = [...otp]
			next[idx] = sanitized
			setOtp(next)
			if (sanitized && idx < otp.length - 1) {
				refs.current[idx + 1]?.focus()
			}
			if (isOtpComplete(next) && isCodeValid(next)) {
				onVerify(next.join('').trim())
			}
		},
		[otp, setOtp, onVerify, isOtpComplete, isCodeValid]
	)

	const handleOtpPaste = useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>, startIdx: number) => {
			e.preventDefault()
			const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '')
			if (!pasted) return
			const arr = pasted.split('').slice(0, otp.length)
			const next = [...otp]
			let lastFilled = -1
			if (arr.length >= next.length) {
				for (let i = 0; i < next.length; i++) {
					next[i] = arr[i]
					lastFilled = i
				}
			} else {
				for (let i = 0; i < arr.length && startIdx + i < next.length; i++) {
					const pos = startIdx + i
					next[pos] = arr[i]
					lastFilled = pos
				}
			}

			setOtp(next)
			const focusIdx = lastFilled >= 0 ? lastFilled : startIdx
			refs.current[focusIdx]?.focus()
			if (isOtpComplete(next) && isCodeValid(next)) onVerify(next.join('').trim())
		},
		[otp, setOtp, onVerify, isOtpComplete, isCodeValid]
	)

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
			if (e.key === 'Backspace') {
				if (!otp[idx] && idx > 0) {
					refs.current[idx - 1]?.focus()
				}
			}
		},
		[otp]
	)

	const focusFirstWithRetries = useCallback(() => {
		const attempts = [0, 50, 150]
		const tryFocus = (i: number) => {
			const el = refs.current[0]
			if (el && !el.disabled) {
				try {
					el.focus()
					el.select?.()
					return
				} catch {
					// ignore and retry
				}
			}
			if (i + 1 < attempts.length) setTimeout(() => tryFocus(i + 1), attempts[i + 1])
		}
		tryFocus(0)
	}, [])

	const clearOtp = useCallback(() => {
		setOtp(Array(otp.length).fill(''))
	}, [otp.length, setOtp])

	return {
		refs,
		handleOtpChange,
		handleOtpPaste,
		handleKeyDown,
		focusFirstWithRetries,
		clearOtp,
	}
}
